// stores/organizationsStore.ts
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { persist } from "zustand/middleware";
import { OrganizationWithoutUsers, OrganizationWithUsers } from "@/lib/types";
import { api } from "@/lib/api";
import { isImpersonating } from "@/stores/impersonationStore";
import { usePatientsStore } from "./patientsStore";
import { useActionItemsStore } from "./actionItemsStore";
import { useConsentsStore } from "./consentsStore";

interface OrganizationsState {
  organizations: OrganizationWithUsers[];
  selectedOrganizationId: string | null;
  setOrganizations: (organizations: OrganizationWithUsers[]) => void;
  addOrganization: (organization: OrganizationWithUsers) => void;
  updateOrganization: (
    id: string,
    updatedOrganization: Partial<OrganizationWithUsers>
  ) => void;
  deleteOrganization: (id: string) => void;
  setSelectedOrganizationId: (id: string | null) => void;
  fetchOrganizations: (
    organizationsFromUserObject: OrganizationWithoutUsers[] | undefined
  ) => Promise<void>;
}

export const useOrganizationsStore = create<OrganizationsState>()(
  persist(
    immer((set, get) => ({
      organizations: [],
      selectedOrganizationId: null,
      setOrganizations: (organizations) =>
        set((state) => {
          state.organizations = organizations;
        }),
      addOrganization: (organization) =>
        set((state) => {
          state.organizations.push(organization);
        }),
      updateOrganization: (id, updatedOrganization) =>
        set((state) => {
          const index = state.organizations.findIndex((org) => org.id === id);
          if (index !== -1) {
            Object.assign(state.organizations[index], updatedOrganization);
          }
        }),
      deleteOrganization: (id) =>
        set((state) => {
          state.organizations = state.organizations.filter(
            (org) => org.id !== id
          );
        }),
      setSelectedOrganizationId: (id) =>
        set((state) => {
          const previousOrgId = state.selectedOrganizationId;
          
          // Only reset stores if the organization is actually changing
          if (previousOrgId !== id && id !== null) {
            console.log(`Organization changed from ${previousOrgId} to ${id}, clearing organization-specific data`);
            
            // Clear organization-specific data from other stores
            usePatientsStore.getState().resetPaginationState();
            useActionItemsStore.getState().clearActionItems();
            useConsentsStore.getState().clearConsents();
          }
          
          state.selectedOrganizationId = id;
        }),
      fetchOrganizations: async (organizationsFromUserObject) => {
        // listOrganizations is GET /organization, which is superuser-only. While
        // viewing the app as someone else the caller is not a superuser, so that
        // request is a guaranteed 403 — skip it and use the org list that came
        // back on the impersonated user instead, which is exactly what they can
        // see. Without this the console fills with failed requests on a path
        // that is working correctly.
        if (isImpersonating()) {
          set((state) => {
            state.organizations = (organizationsFromUserObject ??
              []) as unknown as OrganizationWithUsers[];
          });
          return;
        }
        try {
          const organizations = await api.listOrganizations();
          set((state) => {
            state.organizations = organizations;
            if (organizationsFromUserObject) {
              const mergedOrgs = [...organizations];
              organizationsFromUserObject.forEach((userOrg) => {
                if (!mergedOrgs.some((org) => org.id === userOrg.id)) {
                  mergedOrgs.push(userOrg as unknown as OrganizationWithUsers);
                }
              });
              state.organizations = mergedOrgs;
            }
          });
        } catch (error) {
          console.error("Failed to fetch organizations:", error);
          if (organizationsFromUserObject) {
            set((state) => {
              state.organizations =
                organizationsFromUserObject as unknown as OrganizationWithUsers[];
            });
          }
        }
      },
    })),
    {
      name: "organization-storage",
      partialize: (state) => ({
        selectedOrganizationId: state.selectedOrganizationId,
      }),
    }
  )
);
